import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { Card, CardContent } from '../components/ui/Card'

export const TermsOfServicePage: React.FC = () => {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="mb-4 text-blue-600 hover:text-blue-800"
          >
            ← Back to Home
          </Button>
          
          <div className="text-center mb-6">
            <div className="text-3xl font-bold text-gray-900 mb-2">
              Strat<span className="text-blue-600">IQ</span><span className="text-cyan-600">X</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Terms of Service</h1>
            <p className="text-gray-600">Preview Version - Effective Date: {new Date().toLocaleDateString()}</p>
          </div>
        </div>

        <Card className="shadow-lg border-0 bg-white">
          <CardContent className="p-8 prose prose-blue max-w-none">
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-blue-800 font-medium mb-2">📋 Preview Terms Notice</p>
              <p className="text-blue-700 text-sm">
                These terms apply to the preview version of StratIQX. Final terms of service will be available upon full platform launch.
              </p>
            </div>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-600 mb-6">
              By accessing and using the StratIQX preview platform ("Service"), you agree to be bound by these Terms of Service. 
              If you disagree with any part of these terms, you may not access the Service.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Preview Platform Description</h2>
            <p className="text-gray-600 mb-2">
              StratIQX is a strategic intelligence platform that provides:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6">
              <li>Market intelligence analysis and reporting</li>
              <li>Competitive intelligence insights</li>
              <li>Supply chain intelligence monitoring</li>
              <li>Revenue intelligence optimization</li>
              <li>Industry-specific business intelligence tools</li>
            </ul>
            <p className="text-gray-600 mb-6">
              <strong>Preview Limitations:</strong> This is a preview version. Features may be limited, changed, or unavailable. 
              Data and reports generated are for demonstration and testing purposes.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. User Responsibilities</h2>
            <h3 className="text-lg font-medium text-gray-800 mb-2">Account Security</h3>
            <ul className="list-disc pl-6 text-gray-600 mb-4">
              <li>You are responsible for maintaining the confidentiality of your account</li>
              <li>You must provide accurate and complete information</li>
              <li>You must promptly update any changes to your information</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-2">Acceptable Use</h3>
            <p className="text-gray-600 mb-2">You agree NOT to:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6">
              <li>Use the Service for any unlawful purpose</li>
              <li>Upload malicious files or content</li>
              <li>Attempt to gain unauthorized access to the platform</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Use automated tools to extract data without permission</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Data and Content</h2>
            <h3 className="text-lg font-medium text-gray-800 mb-2">Your Data</h3>
            <p className="text-gray-600 mb-4">
              You retain ownership of all data you upload to StratIQX. By using the Service, you grant us permission to 
              process your data to provide intelligence analysis and reporting services.
            </p>

            <h3 className="text-lg font-medium text-gray-800 mb-2">Intelligence Reports</h3>
            <p className="text-gray-600 mb-6">
              Intelligence reports and insights generated by StratIQX are provided for informational purposes only. 
              You should not rely solely on these reports for critical business decisions without independent verification.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Disclaimers</h2>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-yellow-800 font-medium mb-2">⚠️ Important Disclaimers</p>
              <ul className="list-disc pl-6 text-yellow-700 text-sm">
                <li>The Service is provided "as is" without warranties of any kind</li>
                <li>We do not guarantee the accuracy, completeness, or reliability of intelligence reports</li>
                <li>Market predictions and analysis are estimates based on available data</li>
                <li>Preview features may contain bugs or be incomplete</li>
              </ul>
            </div>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Limitation of Liability</h2>
            <p className="text-gray-600 mb-6">
              To the fullest extent permitted by law, StratIQX shall not be liable for any indirect, incidental, 
              special, consequential, or punitive damages, including without limitation, loss of profits, data, 
              use, goodwill, or other intangible losses.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Privacy and Data Protection</h2>
            <p className="text-gray-600 mb-6">
              Your privacy is important to us. Please review our Privacy Policy to understand how we collect, 
              use, and protect your information. The Privacy Policy is incorporated into these Terms by reference.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Termination</h2>
            <p className="text-gray-600 mb-2">We may terminate or suspend your access immediately, without prior notice, for:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6">
              <li>Breach of these Terms</li>
              <li>Violation of applicable laws</li>
              <li>Conduct that harms other users or the Service</li>
              <li>End of preview period</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Changes to Terms</h2>
            <p className="text-gray-600 mb-6">
              We reserve the right to modify these terms at any time. We will provide notice of material changes. 
              Continued use of the Service after changes constitutes acceptance of the new terms.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Governing Law</h2>
            <p className="text-gray-600 mb-6">
              These Terms shall be interpreted and governed by the laws of [Your Jurisdiction], without regard to 
              conflict of law provisions.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Contact Information</h2>
            <p className="text-gray-600 mb-4">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-gray-700"><strong>Email:</strong> legal@stratiqx.ai</p>
              <p className="text-gray-700"><strong>Address:</strong> StratIQX Legal Department</p>
              <p className="text-gray-700"><strong>Website:</strong> preview.stratiqx.ai</p>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                These preview terms will be updated with comprehensive terms of service upon full platform launch.
              </p>
            </div>

          </CardContent>
        </Card>
      </div>
    </div>
  )
}